import { useMemo, useRef, useState } from "react";
import {
  findLikelyDuplicateLibraryItems,
  type LibraryItem,
  type LibraryItemStatus,
  type LibrarySearchResult,
} from "@/lib/library";
import type { LibraryCategory, LibraryCategoryColorKey } from "@/lib/libraryCategories";
import { LibraryImportDraftRow } from "./LibraryImportDraftRow";
import {
  getBatchDuplicateSignature,
  getDraftKey,
  parseImportText,
  toComparableItem,
  type ImportDraft,
  type ImportMode,
} from "./libraryImportParsing";
import { ImportInputPanel } from "./libraryImport/ImportInputPanel";
import { InvalidDraftsDialog } from "./libraryImport/InvalidDraftsDialog";

export const LibraryImportReview = ({
  items,
  categories,
  initialCategoryIds,
  saving,
  onCreateCategory,
  onSave,
  onOpenExisting,
  onAnnouncementChange,
  onFocusRestored,
  onReviewRowFocusChange,
  myLibraryReturnTo,
}: {
  items: LibraryItem[];
  categories: LibraryCategory[];
  initialCategoryIds: string[];
  saving: boolean;
  onCreateCategory: (values: { name: string; color: LibraryCategoryColorKey }) => Promise<LibraryCategory>;
  onSave: (drafts: Array<LibrarySearchResult & { notes?: string | null }>, categoryIds: string[]) => Promise<void>;
  onOpenExisting: (itemId: string) => void;
  onAnnouncementChange?: (message: string) => void;
  onFocusRestored?: (target: "primary" | "fallback") => void;
  onReviewRowFocusChange?: (validity: "valid" | "invalid" | null) => void;
  myLibraryReturnTo?: string;
}) => {
  const [mode, setMode] = useState<ImportMode>("auto");
  const [rawInput, setRawInput] = useState("");
  const [status, setStatus] = useState<LibraryItemStatus>("to_read");
  const [categoryIds, setCategoryIds] = useState<string[]>(initialCategoryIds);
  const [drafts, setDrafts] = useState<ImportDraft[]>([]);
  const [confirmInvalidOpen, setConfirmInvalidOpen] = useState(false);
  const [highlightedDraftKey, setHighlightedDraftKey] = useState<string | null>(null);
  const [, setHighlightAnnouncement] = useState("");
  const importInputRef = useRef<HTMLTextAreaElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const highlightTimeoutRef = useRef<number | null>(null);

  const duplicateTotal = useMemo(
    () => drafts.reduce((sum, draft) => sum + (draft.validationIssues.length ? 0 : draft.duplicateWarnings.length), 0),
    [drafts],
  );
  const batchDuplicateGroupCount = useMemo(() => {
    const counts = new Map<string, number>();
    drafts.forEach((draft) => {
      if (draft.validationIssues.length) return;
      const signature = getBatchDuplicateSignature(draft);
      counts.set(signature, (counts.get(signature) ?? 0) + 1);
    });
    return Array.from(counts.values()).filter((count) => count > 1).length;
  }, [drafts]);
  const invalidDrafts = useMemo(
    () => drafts.filter((draft) => draft.validationIssues.length > 0),
    [drafts],
  );
  const validDrafts = useMemo(
    () => drafts.filter((draft) => draft.validationIssues.length === 0),
    [drafts],
  );
  const invalidIssueCounts = useMemo(() => {
    const counts = new Map<string, number>();
    invalidDrafts.forEach((draft) => {
      draft.validationIssues.forEach((issue) => {
        counts.set(issue, (counts.get(issue) ?? 0) + 1);
      });
    });
    return Array.from(counts.entries());
  }, [invalidDrafts]);
  const invalidDraftKeys = useMemo(
    () => drafts
      .map((draft, index) => ({ draft, key: getDraftKey(draft, index) }))
      .filter(({ draft }) => draft.validationIssues.length > 0)
      .map(({ key }) => key),
    [drafts],
  );

  const analyzeInput = () => {
    const parsed = parseImportText(rawInput, mode).map((draft) => ({
      ...draft,
      status: draft.status !== "to_read" ? draft.status : status,
    }));

    const draftsWithWarnings = parsed.map((draft, index) => ({
      ...draft,
      duplicateWarnings: draft.validationIssues.length ? [] : findLikelyDuplicateLibraryItems(draft, [
        ...items,
        ...parsed
          .slice(0, index)
          .filter((entry) => entry.validationIssues.length === 0)
          .map((entry, parsedIndex) => toComparableItem(entry, parsedIndex)),
      ]),
    }));
    setDrafts(draftsWithWarnings);
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setRawInput(text);
    setMode("csv");
  };

  const submitImport = (nextDrafts: ImportDraft[]) =>
    void onSave(
      nextDrafts.map(({ importLabel: _l, duplicateWarnings: _d, validationIssues: _v, ...draft }) => draft),
      categoryIds,
    );

  const handleImportClick = () => {
    if (!drafts.length || saving) return;
    if (invalidDrafts.length) {
      setConfirmInvalidOpen(true);
      return;
    }
    submitImport(drafts);
  };

  const handleExcludeInvalidRows = () => {
    setConfirmInvalidOpen(false);
    submitImport(validDrafts);
  };

  const focusDraft = (draftKey: string, label: string) => {
    setConfirmInvalidOpen(false);
    setHighlightedDraftKey(draftKey);
    const nextAnnouncement = `Highlighted review row ${label}.`;
    setHighlightAnnouncement(nextAnnouncement);
    onAnnouncementChange?.(nextAnnouncement);

    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
    }

    requestAnimationFrame(() => {
      rowRefs.current[draftKey]?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      rowRefs.current[draftKey]?.focus?.();
    });

    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedDraftKey((current) => (current === draftKey ? null : current));
      setHighlightAnnouncement((current) => (current === `Highlighted review row ${label}.` ? "" : current));
      onAnnouncementChange?.("");
    }, 2000);
  };

  const handleJumpToDraft = (draftKey: string, label: string) => {
    focusDraft(draftKey, label);
  };

  const restoreFocusToImportControls = () => {
    requestAnimationFrame(() => {
      if (importInputRef.current && !importInputRef.current.disabled) {
        importInputRef.current.focus();
        onFocusRestored?.("primary");
        return;
      }

      const fallbackTarget = rootRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      fallbackTarget?.focus();
      if (fallbackTarget) {
        onFocusRestored?.("fallback");
      }
    });
  };

  const handleHighlightedRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, draftKey: string, label: string) => {
    if (event.key === "Escape" && invalidDraftKeys.includes(draftKey)) {
      event.preventDefault();
      event.stopPropagation();
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      setHighlightedDraftKey(null);
      setHighlightAnnouncement("");
      onAnnouncementChange?.("");
      event.currentTarget.blur();
      restoreFocusToImportControls();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (["Enter", " "].includes(event.key)) {
      event.preventDefault();
      focusDraft(draftKey, label);
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Tab"].includes(event.key)) return;

    const currentIndex = invalidDraftKeys.indexOf(draftKey);
    if (currentIndex === -1) return;

    const nextIndex = event.key === "ArrowDown"
      ? currentIndex + 1
      : event.key === "ArrowUp"
        ? currentIndex - 1
        : event.shiftKey
          ? currentIndex - 1
          : currentIndex + 1;
    const nextDraftKey = invalidDraftKeys[nextIndex];
    if (!nextDraftKey) return;

    const nextDraft = drafts.find((draft, index) => getDraftKey(draft, index) === nextDraftKey);
    if (!nextDraft) return;

    event.preventDefault();
    focusDraft(nextDraftKey, nextDraft.title || nextDraft.importLabel || label);
  };

  const handleJumpTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, draftKey: string, label: string) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleJumpToDraft(draftKey, label);
  };

  return (
    <div ref={rootRef} className="space-y-4">
      <ImportInputPanel
        mode={mode}
        setMode={setMode}
        rawInput={rawInput}
        setRawInput={setRawInput}
        status={status}
        setStatus={setStatus}
        categories={categories}
        categoryIds={categoryIds}
        setCategoryIds={setCategoryIds}
        saving={saving}
        draftCount={drafts.length}
        duplicateBadgeCount={batchDuplicateGroupCount || duplicateTotal}
        invalidCount={invalidDrafts.length}
        onAnalyze={analyzeInput}
        onUploadFile={handleFile}
        onImportClick={handleImportClick}
        onCreateCategory={onCreateCategory}
        importInputRef={importInputRef}
      />

      <InvalidDraftsDialog
        open={confirmInvalidOpen}
        onOpenChange={setConfirmInvalidOpen}
        invalidDrafts={invalidDrafts}
        validDraftCount={validDrafts.length}
        invalidIssueCounts={invalidIssueCounts}
        drafts={drafts}
        onJump={handleJumpToDraft}
        onJumpKeyDown={handleJumpTriggerKeyDown}
        onExclude={handleExcludeInvalidRows}
      />

      {drafts.length ? (
        <div className="space-y-3">
          {drafts.map((draft, index) => {
            const draftKey = getDraftKey(draft, index);
            return (
              <LibraryImportDraftRow
                key={draftKey}
                ref={(element) => {
                  rowRefs.current[draftKey] = element;
                }}
                draft={draft}
                draftKey={draftKey}
                highlighted={highlightedDraftKey === draftKey}
                onFocusValidity={(validity) => onReviewRowFocusChange?.(validity)}
                onKeyDown={(event) =>
                  handleHighlightedRowKeyDown(event, draftKey, draft.title || draft.importLabel)
                }
                onOpenExisting={onOpenExisting}
                myLibraryReturnTo={myLibraryReturnTo}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
